import React from 'react';
import { User, Settings, CreditCard, CircleHelp, LogOut, X, ChevronRight, Crown } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useUserStore } from '../../stores/userStore';
import { toast } from 'sonner';
import { useI18n } from '../../i18n';

interface ProfileContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onShowSettings?: () => void;
  onShowHelp?: () => void;
}

export function ProfileContextMenu({ isOpen, onClose, onShowSettings, onShowHelp }: ProfileContextMenuProps) {
  const { t } = useI18n();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { profile } = useUserStore();
  
  // Get user info
  const userName = user?.firstName || user?.username || profile?.display_name || t('profile.defaultUser');
  const userEmail = user?.primaryEmailAddress?.emailAddress || profile?.email || t('profile.noEmail');
  const userTier = profile?.subscription_tier || 'free';
  const isPro = userTier === 'pro' || userTier === 'paid' || userTier === 'premium';
  
  const handleLogout = async () => {
    try {
      await signOut();
      onClose();
    } catch (error) {
      console.error('Logout error:', error);
      toast.error(t('profile.toast.logoutFailed'));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Dimming Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-50 md:hidden"
            onClick={onClose}
          />

          {/* Context Menu */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed start-4 end-4 top-1/2 -translate-y-1/2 z-50 md:hidden max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/50">
                <h2 className="text-xl font-semibold text-[#434343]">{t('profile.title')}</h2>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200/60 hover:bg-gray-300/60 active:scale-95 transition-all"
                >
                  <X className="h-5 w-5 text-[#434343]" />
                </button>
              </div>

              {/* User Info */}
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center shadow-lg shadow-[#7367FF]/30 overflow-hidden">
                    {user?.imageUrl ? (
                      <img src={user.imageUrl} alt={userName} className="w-full h-full object-cover" />
                    ) : (
                    <User className="h-8 w-8 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#434343]">{userName}</p>
                    <p className="text-[13px] text-[#434343]/60" dir="ltr">{userEmail}</p>
                    <div className={`mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${
                      isPro 
                        ? 'bg-gradient-to-r from-[#7367FF]/10 to-[#2DD2FE]/10 border border-[#7367FF]/20' 
                        : 'bg-gray-100 border border-gray-200'
                    }`}>
                      {isPro && <Crown className="w-3 h-3 text-[#7367FF]" />}
                      <span className={`text-[11px] font-medium ${isPro ? 'text-[#7367FF]' : 'text-gray-600'}`}>
                        {isPro ? t('common.proPlan') : t('common.freePlan')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="overflow-y-auto flex-1 px-4 pb-4">
                <div className="space-y-2">
                  <MenuItem
                    icon={Settings}
                    label={t('common.settings')}
                    onClick={() => {
                      onShowSettings?.();
                      onClose();
                    }}
                  />
                  <MenuItem
                    icon={CreditCard}
                    label={t('profile.menu.subscription')}
                    onClick={() => {
                      toast.info(isPro ? t('profile.toast.onPro') : t('profile.toast.upgradeHint'));
                      onClose();
                    }}
                    badge={isPro ? t('common.pro') : t('common.free')}
                  />
                  <MenuItem
                    icon={CircleHelp}
                    label={t('common.helpAndSupport')}
                    onClick={() => {
                      onShowHelp?.();
                      onClose();
                    }}
                  />
                  
                  {/* Divider */}
                  <div className="py-2">
                    <div className="h-px bg-gray-200/50" />
                  </div>

                  <MenuItem
                    icon={LogOut}
                    label={t('common.logOut')}
                    onClick={handleLogout}
                    variant="danger"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface MenuItemProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  badge?: string;
  variant?: 'default' | 'danger';
}

function MenuItem({ icon: Icon, label, onClick, badge, variant = 'default' }: MenuItemProps) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-3.5 rounded-2xl bg-white border border-gray-200/50 hover:bg-gray-50 active:scale-[0.98] transition-all duration-200 shadow-sm"
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
        variant === 'danger' 
          ? 'bg-[#ff3b30]/10' 
          : 'bg-gradient-to-br from-[#7367FF]/10 to-[#2DD2FE]/10'
      }`}>
        <Icon className={`h-5 w-5 ${
          variant === 'danger' ? 'text-[#ff3b30]' : 'text-[#7367FF]'
        }`} />
      </div>
      <span className={`flex-1 text-start text-[15px] font-medium ${
        variant === 'danger' ? 'text-[#ff3b30]' : 'text-[#434343]'
      }`}>
        {label}
      </span>
      {badge && (
        <div className="px-2 py-1 rounded-full bg-gradient-to-r from-[#7367FF]/10 to-[#2DD2FE]/10 border border-[#7367FF]/20">
          <span className="text-[11px] font-medium text-[#7367FF]">{badge}</span>
        </div>
      )}
      <ChevronRight className="h-4 w-4 text-[#434343]/30 rtl-flip" />
    </button>
  );
}